// Tenantra Inc. - API Keys Page (Microsoft 365 Admin Style)
// Enterprise Feature - API Access Management
import { useState, useEffect } from 'react';
import { 
  Key, Plus, Copy, Trash2, Eye, EyeOff, Clock, 
  CheckCircle, XCircle, AlertTriangle, Loader2,
  BarChart3, RefreshCw, X, Shield, Code, ExternalLink,
  Activity, Calendar, Info, ChevronRight, Lock, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api from '@/services/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';

// Scope badge component
const ScopeBadge = ({ scope }) => {
  const styles = {
    read_only: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Read Only' },
    read_write: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Read/Write' },
    full_access: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', label: 'Full Access' }
  };
  
  const style = styles[scope] || styles.read_only;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`}>
      <Shield className="w-3 h-3" />
      {style.label}
    </span>
  );
};

// Status indicator
const StatusIndicator = ({ isActive }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    {isActive ? 'Active' : 'Revoked'}
  </span>
);

// Create Key Modal
const CreateKeyModal = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('read_only');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  
  if (!isOpen) return null;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        name,
        description: description || null,
        scope,
        expires_in_days: expiresInDays && expiresInDays !== 'never' ? parseInt(expiresInDays) : null
      };
      
      const response = await api.post('/api-keys', payload);
      setCreatedKey(response.data);
      toast.success('API key created successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopy = () => {
    if (createdKey?.api_key) {
      navigator.clipboard.writeText(createdKey.api_key);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleClose = () => {
    if (createdKey) {
      onSuccess();
    }
    setName('');
    setDescription('');
    setScope('read_only');
    setExpiresInDays('');
    setCreatedKey(null);
    onClose();
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={handleClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-xl shadow-2xl z-50 overflow-hidden">
        {!createdKey ? (
          <>
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Create API Key</h2>
                    <p className="text-sm text-slate-500">Generate a new key for API access</p>
                  </div>
                </div>
                <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Key Name *</Label>
                <Input 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Production API Key"
                  required
                  minLength={2}
                  maxLength={100}
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Description (optional)</Label>
                <Input 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Used for property management integration"
                  maxLength={500}
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Access Scope</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="read_only">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-blue-600" />
                        <div>
                          <span className="font-medium">Read Only</span>
                          <p className="text-xs text-slate-500">View properties, tenants, reports</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="read_write">
                      <div className="flex items-center gap-2">
                        <Code className="w-4 h-4 text-amber-600" />
                        <div>
                          <span className="font-medium">Read/Write</span>
                          <p className="text-xs text-slate-500">Create and modify data</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="full_access">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-600" />
                        <div>
                          <span className="font-medium">Full Access</span>
                          <p className="text-xs text-slate-500">All operations including bulk imports</p>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Expiration (optional)</Label>
                <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Never expires" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="never">Never expires</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1 h-11">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 text-white">
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  Create Key
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="p-5 border-b border-slate-200 bg-emerald-50">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">API Key Created</h2>
                  <p className="text-sm text-slate-600">Copy your key now - it won&apos;t be shown again!</p>
                </div>
              </div>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> This is the only time you&apos;ll see this key. 
                    Store it securely - you cannot retrieve it later.
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Your API Key</Label>
                <div className="relative">
                  <Input 
                    value={createdKey.api_key}
                    readOnly
                    className="h-12 pr-20 font-mono text-sm bg-slate-50"
                  />
                  <Button 
                    type="button"
                    onClick={handleCopy}
                    className={`absolute right-1 top-1 h-10 px-4 ${copied ? 'bg-emerald-600' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 py-2">
                <div>
                  <p className="text-xs text-slate-500">Name</p>
                  <p className="font-medium text-slate-900">{createdKey.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Scope</p>
                  <ScopeBadge scope={createdKey.scope} />
                </div>
              </div>
              
              <Button onClick={handleClose} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// Key row component
const KeyRow = ({ apiKey, onRevoke, onViewUsage }) => {
  const [showPrefix, setShowPrefix] = useState(false);
  
  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0">
      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${apiKey.is_active ? 'bg-teal-100' : 'bg-slate-100'}`}>
        <Key className={`w-5 h-5 ${apiKey.is_active ? 'text-teal-600' : 'text-slate-400'}`} />
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900">{apiKey.name}</p>
          <StatusIndicator isActive={apiKey.is_active} />
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <button
            onClick={() => setShowPrefix(!showPrefix)}
            className="text-xs text-slate-500 font-mono hover:text-slate-700 flex items-center gap-1"
          >
            {showPrefix ? apiKey.key_prefix : '••••••••••••••••'}
            {showPrefix ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
          <span className="text-xs text-slate-400">•</span>
          <span className="text-xs text-slate-500">{apiKey.usage_count.toLocaleString()} requests</span>
        </div>
      </div>
      
      {/* Scope */}
      <div className="hidden sm:block">
        <ScopeBadge scope={apiKey.scope} />
      </div>
      
      {/* Last used */}
      <div className="hidden md:block text-sm text-slate-500 w-28">
        {apiKey.last_used_at 
          ? formatDate(apiKey.last_used_at) 
          : 'Never used'}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 px-2"
          onClick={() => onViewUsage(apiKey)}
        >
          <BarChart3 className="w-4 h-4 text-slate-400" />
        </Button>
        {apiKey.is_active && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onRevoke(apiKey)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Usage Modal
const UsageModal = ({ isOpen, onClose, apiKey }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchUsage = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api-keys/${apiKey.id}/usage`);
        setUsage(response.data);
      } catch (err) {
        toast.error('Failed to fetch usage data');
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen && apiKey) {
      fetchUsage();
    }
  }, [isOpen, apiKey]);
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-xl shadow-2xl z-50 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">API Key Usage</h2>
              <p className="text-sm text-slate-500">{apiKey?.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>
        
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : usage ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-slate-900">{usage.total_requests.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">Total Requests</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-teal-700">{usage.requests_today.toLocaleString()}</div>
                  <div className="text-xs text-teal-600">Today</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{usage.requests_this_month.toLocaleString()}</div>
                  <div className="text-xs text-blue-600">This Month</div>
                </div>
              </div>
              
              {usage.last_used_at && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4" />
                  Last used: {new Date(usage.last_used_at).toLocaleString()}
                </div>
              )}
              
              {Object.keys(usage.endpoints_called).length > 0 && (
                <div>
                  <h3 className="font-medium text-slate-800 mb-2">Top Endpoints (Last 30 days)</h3>
                  <div className="bg-slate-50 rounded-lg overflow-hidden">
                    {Object.entries(usage.endpoints_called).map(([endpoint, count]) => (
                      <div key={endpoint} className="flex items-center justify-between px-3 py-2 border-b border-slate-200 last:border-0">
                        <code className="text-xs text-slate-700">{endpoint}</code>
                        <span className="text-xs font-medium text-slate-500">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No usage data available</p>
          )}
        </div>
        
        <div className="p-5 border-t border-slate-200 bg-slate-50">
          <Button onClick={onClose} className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white">
            Close
          </Button>
        </div>
      </div>
    </>
  );
};

export default function APIKeysPage() {
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [docs, setDocs] = useState(null);
  const [billingPlan, setBillingPlan] = useState(null);
  
  // Check billing plan on mount
  useEffect(() => {
    checkBillingPlan();
  }, []);
  
  const checkBillingPlan = async () => {
    try {
      const response = await api.get('/billing/current-plan');
      setBillingPlan(response.data);
      fetchData();
    } catch (err) {
      console.error('Failed to check billing plan:', err);
      fetchData();
    }
  };
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [keysRes, summaryRes] = await Promise.all([
        api.get('/api-keys'),
        api.get('/api-keys/usage/summary')
      ]);
      setApiKeys(keysRes.data);
      setUsageSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRevoke = async (key) => {
    if (!confirm(`Are you sure you want to revoke "${key.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/api-keys/${key.id}`);
      toast.success('API key revoked');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke key');
    }
  };
  
  const handleViewUsage = (key) => {
    setSelectedKey(key);
    setShowUsageModal(true);
  };
  
  const fetchDocs = async () => {
    try {
      const response = await api.get('/api-keys/docs/endpoints');
      setDocs(response.data);
      setShowDocs(true);
    } catch (err) {
      toast.error('Failed to load documentation');
    }
  };
  
  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">API Access</h1>
          <p className="text-sm text-slate-500">Manage API keys for programmatic access to your data</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={fetchDocs}
            className="h-10"
          >
            <Code className="w-4 h-4 mr-2" /> API Docs
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="h-10 bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Key
          </Button>
        </div>
      </div>
      
      {/* Usage Summary */}
      {usageSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Key className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{usageSummary.active_keys}</p>
                <p className="text-xs text-slate-500">Active Keys</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{usageSummary.total_requests.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Total Requests</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{usageSummary.requests_today.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{usageSummary.requests_this_month.toLocaleString()}</p>
                <p className="text-xs text-slate-500">This Month</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* API Keys List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Your API Keys</h3>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
            {apiKeys.filter(k => k.is_active).length} active / {apiKeys.length} total
          </span>
        </div>
        
        {apiKeys.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-medium text-slate-900 mb-1">No API Keys Yet</h3>
            <p className="text-sm text-slate-500 mb-4">Create your first API key to start using the Tenantra API</p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Create API Key
            </Button>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div className="hidden sm:flex items-center gap-4 px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <div className="w-10"></div>
              <div className="flex-1">Key</div>
              <div className="w-24">Scope</div>
              <div className="hidden md:block w-28">Last Used</div>
              <div className="w-20"></div>
            </div>
            
            {/* Keys */}
            <div className="divide-y divide-slate-100">
              {apiKeys.map((key) => (
                <KeyRow 
                  key={key.id}
                  apiKey={key}
                  onRevoke={handleRevoke}
                  onViewUsage={handleViewUsage}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Quick Start Guide */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-teal-600" />
          Quick Start Guide
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">1</div>
              <span className="font-medium text-slate-800 text-sm">Create an API Key</span>
            </div>
            <p className="text-xs text-slate-500">Click &quot;Create Key&quot; and choose an appropriate scope for your use case.</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">2</div>
              <span className="font-medium text-slate-800 text-sm">Add to Request Header</span>
            </div>
            <p className="text-xs text-slate-500">Include your key in the <code className="bg-slate-100 px-1 rounded">X-API-Key</code> header of each request.</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">3</div>
              <span className="font-medium text-slate-800 text-sm">Make API Calls</span>
            </div>
            <p className="text-xs text-slate-500">Use standard REST calls to access properties, tenants, reports, and more.</p>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-slate-900 rounded-lg">
          <p className="text-xs text-slate-400 mb-1">Example Request:</p>
          <code className="text-sm text-emerald-400 font-mono">
            curl -H &quot;X-API-Key: tntra_live_xxx...&quot; https://api.tenantra.ca/api/properties
          </code>
        </div>
      </div>
      
      {/* Modals */}
      <CreateKeyModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchData}
      />
      
      <UsageModal 
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        apiKey={selectedKey}
      />
      
      {/* API Docs Modal */}
      {showDocs && docs && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowDocs(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl max-h-[80vh] bg-white rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">API Documentation</h2>
              <button onClick={() => setShowDocs(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Authentication */}
              <div>
                <h3 className="font-medium text-slate-800 mb-2">Authentication</h3>
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <p className="text-slate-600 mb-2">Add your API key to the request header:</p>
                  <code className="block bg-slate-900 text-emerald-400 p-2 rounded text-xs">
                    {docs.authentication.header}: {docs.authentication.example.split(': ')[1]}
                  </code>
                </div>
              </div>
              
              {/* Rate Limits */}
              <div>
                <h3 className="font-medium text-slate-800 mb-2">Rate Limits</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-slate-600">Per Minute</p>
                    <p className="font-bold text-slate-900">{docs.rate_limits.requests_per_minute.toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-slate-600">Per Day</p>
                    <p className="font-bold text-slate-900">{docs.rate_limits.requests_per_day.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              {/* Endpoints */}
              <div>
                <h3 className="font-medium text-slate-800 mb-2">Available Endpoints</h3>
                {Object.entries(docs.endpoints).map(([category, endpoints]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-medium text-teal-700 uppercase tracking-wider mb-2">{category}</h4>
                    <div className="bg-slate-50 rounded-lg overflow-hidden">
                      {Object.entries(endpoints).map(([path, info]) => (
                        <div key={path} className="flex items-center justify-between px-3 py-2 border-b border-slate-200 last:border-0">
                          <div>
                            <code className="text-xs text-slate-800">{path}</code>
                            <p className="text-xs text-slate-500">{info.description}</p>
                          </div>
                          <ScopeBadge scope={info.scope_required} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
